import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { UserFilesBatchingActions, UserFilesBatchedOperationKind, UserFilesBatchedOperationStatus } from './slice'
import { selectActiveUserFilesOperation } from './selectors'
import { executeUserFilesBatchedOperation, getUserFilesOperationErrorMessage } from '@/user_files/batching/utils/execute-user-files-batched-operation'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'

export const userFilesBatchingListenerMiddleware = createListenerMiddleware()
const startListener = userFilesBatchingListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

startListener({
  matcher: isAnyOf(UserFilesBatchingActions.enqueueMoveSelection, UserFilesBatchingActions.enqueueDeleteSelection),
  effect: async (_action, listenerApi) => {
    const op = selectActiveUserFilesOperation(listenerApi.getState())
    if (!op || op.status !== UserFilesBatchedOperationStatus.Queued) return

    listenerApi.dispatch(UserFilesBatchingActions.operationStarted({ operationId: op.id }))

    const active = selectActiveUserFilesOperation(listenerApi.getState())
    if (!active || active.id !== op.id) return

    const foldersToRefresh = Array.from(new Set([active.sourceFolderId, active.destinationFolderId].filter((id): id is string => Boolean(id))))

    try {
      const foldersRecord = listenerApi.getState().userFoldersState.folders

      const result = await executeUserFilesBatchedOperation({
        kind: active.kind,
        destinationFolderId: active.destinationFolderId,
        fileIds: active.fileIds,
        folderIds: active.folderIds,
        filePreconditions: active.filePreconditions,
        folderPreconditions: active.folderPreconditions,
        destinationFolderPrecondition: active.destinationFolderPrecondition,
        isRootFolderId: (folderId) => Boolean(foldersRecord[folderId]?.data.is_root),
        onProgress: (count) => {
          listenerApi.dispatch(UserFilesBatchingActions.operationProgressUpdated({ operationId: active.id, processedItems: count }))
        },
      })

      // Keep upload rows aligned with successful file operations so conflict detection does not reserve stale names.
      if (result.kind === UserFilesBatchedOperationKind.DeleteSelection) {
        listenerApi.dispatch(
          UppyFileUploadActions.filesRemovedByServerFileIds({
            serverFileIds: [...result.aggregate.deleted_file_ids, ...result.aggregate.unchanged_file_ids],
          })
        )
      }
      if (result.kind === UserFilesBatchedOperationKind.MoveSelection) {
        listenerApi.dispatch(
          UppyFileUploadActions.filesMovedByServerFileIds({
            serverFileIds: [...result.aggregate.moved_file_ids, ...result.aggregate.unchanged_file_ids],
            folderId: result.aggregate.destination_folder_id,
          })
        )
      }

      listenerApi.dispatch(UserFilesBatchingActions.operationSucceeded({ operationId: active.id }))
    } catch (cause) {
      const errorMessage = getUserFilesOperationErrorMessage({ kind: active.kind, cause })
      listenerApi.dispatch(UserFilesBatchingActions.operationFailed({ operationId: active.id, errorMessage }))
    } finally {
      // If the user navigated away before completion, mark the source folder's
      // child-folder cache stale so revisiting it triggers a refetch.
      listenerApi.dispatch(UserFoldersActions.invalidateFolderChildren({ folderId: active.sourceFolderId }))

      // Server is the source of truth: refresh on success AND failure.
      // Only refresh folders that are still relevant (current folder and destination folder)
      // to avoid unnecessary network requests after user navigation.
      const currentFolderId = selectCurrentFolderId(listenerApi.getState())
      const relevantFolders = foldersToRefresh.filter((folderId) => {
        // Always refresh the current folder
        if (folderId === currentFolderId) return true
        // Also refresh if user navigated into the destination folder
        if (folderId === active.destinationFolderId) return true
        return false
      })

      relevantFolders.forEach((folderId) => {
        listenerApi.dispatch(UserFoldersActions.fetchFolderChildren({ folderId, force: true }))
        listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
      })
    }
  },
})
